import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'confiesalo_preferences';
const BOOKMARKS_KEY = 'confiesalo_bookmarks';

// Tipos de temas
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark'
};

// Tipos de tamaño de fuente
export const FONT_SIZES = {
  SMALL: 'small',
  NORMAL: 'normal',
  LARGE: 'large'
};

// Valores de tamaño en rem
export const FONT_SIZE_VALUES = {
  [FONT_SIZES.SMALL]: 0.875,   // 14px
  [FONT_SIZES.NORMAL]: 1,       // 16px (base)
  [FONT_SIZES.LARGE]: 1.25      // 20px
};

/**
 * Hook para manejar las preferencias del usuario
 * Guarda tema y tamaño de fuente en localStorage
 */
export const useUserPreferences = () => {
  const [theme, setTheme] = useState(THEMES.LIGHT);
  const [fontSize, setFontSize] = useState(FONT_SIZES.NORMAL);
  const [bookmarks, setBookmarks] = useState([]);

  // Cargar preferencias al montar
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const prefs = JSON.parse(stored);
        if (prefs.theme) setTheme(prefs.theme);
        if (prefs.fontSize) setFontSize(prefs.fontSize);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }

    // Cargar marcadores
    try {
      const storedBookmarks = localStorage.getItem(BOOKMARKS_KEY);
      if (storedBookmarks) {
        setBookmarks(JSON.parse(storedBookmarks));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  }, []);

  // Aplicar tema al body
  useEffect(() => {
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${theme}`);
  }, [theme]);

  // Aplicar tamaño de fuente al body
  useEffect(() => {
    document.body.classList.remove('font-small', 'font-normal', 'font-large');
    document.body.classList.add(`font-${fontSize}`);
    document.body.style.fontSize = FONT_SIZE_VALUES[fontSize] + 'rem';
  }, [fontSize]);

  // Cambiar tema
  const changeTheme = useCallback((newTheme) => {
    setTheme(newTheme);
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const prefs = stored ? JSON.parse(stored) : {};
      prefs.theme = newTheme;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  }, []);

  // Cambiar tamaño de fuente
  const changeFontSize = useCallback((newSize) => {
    setFontSize(newSize);
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const prefs = stored ? JSON.parse(stored) : {};
      prefs.fontSize = newSize;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (error) {
      console.error('Error saving font size:', error);
    }
  }, []);

  // Agregar marcador
  const addBookmark = useCallback((bookmark) => {
    const newBookmark = {
      id: Date.now().toString(),
      ...bookmark,
      createdAt: new Date().toISOString()
    };
    setBookmarks(prev => {
      const exists = prev.some(b => b.type === newBookmark.type && b.id === newBookmark.id);
      if (exists) return prev;
      const updated = [...prev, newBookmark];
      try {
        localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving bookmarks:', error);
      }
      return updated;
    });
  }, []);

  // Eliminar marcador
  const removeBookmark = useCallback((bookmarkId) => {
    setBookmarks(prev => {
      const updated = prev.filter(b => b.id !== bookmarkId);
      try {
        localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving bookmarks:', error);
      }
      return updated;
    });
  }, []);

  // Verificar si un elemento está marcado
  const isBookmarked = useCallback((type, itemId) => {
    return bookmarks.some(b => b.type === type && b.itemId === itemId);
  }, [bookmarks]);

  // Alternar marcador
  const toggleBookmark = useCallback((bookmark) => {
    if (isBookmarked(bookmark.type, bookmark.itemId)) {
      const existing = bookmarks.find(b => b.type === bookmark.type && b.itemId === bookmark.itemId);
      if (existing) {
        removeBookmark(existing.id);
      }
    } else {
      addBookmark(bookmark);
    }
  }, [addBookmark, isBookmarked, removeBookmark, bookmarks]);

  // Limpiar todos los marcadores
  const clearBookmarks = useCallback(() => {
    setBookmarks([]);
    try {
      localStorage.removeItem(BOOKMARKS_KEY);
    } catch (error) {
      console.error('Error clearing bookmarks:', error);
    }
  }, []);

  return {
    // Estado
    theme,
    fontSize,
    bookmarks,

    // Tema
    changeTheme,
    isDark: theme === THEMES.DARK,
    isLight: theme === THEMES.LIGHT,

    // Tamaño de fuente
    changeFontSize,
    isSmall: fontSize === FONT_SIZES.SMALL,
    isNormal: fontSize === FONT_SIZES.NORMAL,
    isLarge: fontSize === FONT_SIZES.LARGE,

    // Marcadores
    addBookmark,
    removeBookmark,
    isBookmarked,
    toggleBookmark,
    clearBookmarks
  };
};

export default useUserPreferences;
